/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { FOR_YOU_TAB_ID, type RovoTabsProps } from './constants';

const styles = cssMap({
	tabsContainer: {
		width: '100%',
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	tabListWrapper: {
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
	tabListContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		position: 'relative',
	},
	tabIndicator: {
		position: 'absolute',
		top: 0,
		left: 0,
		height: '100%',
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.neutral'),
		transition: 'transform 200ms ease-in-out, width 200ms ease-in-out',
	},
	tab: {
		paddingBlock: token('space.075'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		borderRadius: token('radius.full'),
		backgroundColor: 'transparent',
		cursor: 'pointer',
		border: 'none',
	},
	tabContentWrapper: {
		flex: '1',
		minHeight: '0px',
		overflow: 'hidden',
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
	},
	// First tab panel stays in normal flow to maintain modal height when switching.
	// flex:1 so it fills the area—For You overlay then gets full height too.
	// Scrollbar: subtle by default, visible on hover (matches editor Chromeless / design-system pattern).
	primaryPanel: {
		flex: '1',
		minHeight: '0px',
		width: '100%',
		overflow: 'auto',
		scrollbarColor: `${token('color.border')} transparent`,
		// `stable` reserves a right gutter even when there is no scrollbar, which makes
		// full-width content (e.g. For You thread cards) look off-center in the mini modal.
		scrollbarGutter: 'auto',
		scrollbarWidth: 'thin',
	},
	panelHidden: {
		visibility: 'hidden',
		pointerEvents: 'none',
	},
	panelDisplayNone: {
		display: 'none',
	},
	// Non-primary panels overlay on top. Same scrollbar pattern as primaryPanel.
	overlayPanel: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		overflow: 'auto',
		scrollbarColor: `${token('color.border')} transparent`,
		scrollbarGutter: 'auto',
		scrollbarWidth: 'thin',
	},
});

export const RovoTabs = ({
	tabs,
	selectedTab,
	onTabChange,
	hideTabStrip = false,
}: RovoTabsProps): JSX.Element => {
	const onSelectTab = useCallback(
		(tabId: string) => {
			onTabChange(tabId);
		},
		[onTabChange],
	);
	const tabRefsMap = useRef(new Map<string, HTMLButtonElement>());
	const [indicatorStyle, setIndicatorStyle] = useState<{
		width: number | string;
		transform: string;
	}>({
		width: 0,
		transform: 'translateX(0)',
	});

	const setTabRef = useCallback((id: string, el: HTMLButtonElement | null) => {
		if (el) {
			tabRefsMap.current.set(id, el);
		} else {
			tabRefsMap.current.delete(id);
		}
	}, []);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (tabs.length === 0) {
				return;
			}

			const currentIndex = tabs.findIndex((t) => t.id === selectedTab);
			if (currentIndex === -1) {
				return;
			}

			let nextIndex: number | null = null;

			if (e.key === 'ArrowRight') {
				nextIndex = (currentIndex + 1) % tabs.length;
			} else if (e.key === 'ArrowLeft') {
				nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
			}

			if (nextIndex !== null) {
				e.preventDefault();
				const nextTab = tabs[nextIndex];
				onSelectTab(nextTab.id);
				tabRefsMap.current.get(nextTab.id)?.focus();
			}
		},
		[tabs, selectedTab, onSelectTab],
	);

	// Calculate indicator position and width after DOM updates
	useLayoutEffect(() => {
		const selectedRef = tabRefsMap.current.get(selectedTab);
		if (selectedRef) {
			setIndicatorStyle({
				width: `${selectedRef.offsetWidth}px`,
				transform: `translateX(${selectedRef.offsetLeft}px)`,
			});
		}
	}, [selectedTab]);

	// Lazily mount tab content: a tab's content is only rendered after it has first been selected.
	// This prevents expensive data fetches (e.g. For You GraphQL/REST calls) from firing before
	// the user has actually opened that tab. The initial `selectedTab` is seeded so its content
	// renders immediately. When `selectedTab` changes to a new (unvisited) tab, calling setState
	// during render causes React to re-render synchronously without painting the intermediate
	// null state — this is the React-recommended "store information from previous renders" pattern.
	const [visitedTabs, setVisitedTabs] = useState<ReadonlySet<string>>(() => new Set([selectedTab]));
	if (!visitedTabs.has(selectedTab)) {
		setVisitedTabs((prev) => new Set([...prev, selectedTab]));
	}

	const primaryTab = tabs[0];
	const forYouTab = tabs.find((t) => t.id === FOR_YOU_TAB_ID);
	const isForYouSelected = selectedTab === FOR_YOU_TAB_ID;
	// When For You is selected, render it as the in-flow primary panel so it fills the modal height.
	// Otherwise the overlay (position:absolute) can fail to inherit height from a visibility:hidden primary.
	const flowTab = isForYouSelected && forYouTab ? forYouTab : primaryTab;
	const overlayTabs =
		isForYouSelected && forYouTab ? tabs.filter((t) => t.id !== FOR_YOU_TAB_ID) : tabs.slice(1);

	return (
		<Box xcss={styles.tabsContainer}>
			{/* Skip rendering the tablist header when hideTabStrip is true, but
			    keep the tab-body panels mounted below so drill-in state in the selected
			    tab (e.g. RovoConversationActions nested action view) is preserved. */}
			{!hideTabStrip && (
				<Box xcss={styles.tabListWrapper}>
					<Box xcss={styles.tabListContainer} role="tablist" onKeyDown={handleKeyDown}>
						<Box
							xcss={styles.tabIndicator}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic styles based on tab position
							style={indicatorStyle}
							aria-hidden="true"
						/>
						{tabs.map((tab) => (
							<Pressable
								key={tab.id}
								xcss={styles.tab}
								role="tab"
								aria-selected={selectedTab === tab.id}
								aria-controls={`panel-${tab.id}`}
								id={`rovo-tab-${tab.id}`}
								tabIndex={0}
								onClick={() => onSelectTab(tab.id)}
								testId={`rovo-tab-${tab.id}`}
								ref={(el) => setTabRef(tab.id, el)}
							>
								<Text
									weight="medium"
									color={selectedTab === tab.id ? 'color.text' : 'color.text.subtlest'}
								>
									{tab.label}
								</Text>
							</Pressable>
						))}
					</Box>
				</Box>
			)}
			<Box xcss={styles.tabContentWrapper}>
				{/* In-flow panel: For You when selected (so it fills height), otherwise Suggestions */}
				{flowTab && (
					<Box
						xcss={cx(styles.primaryPanel, selectedTab !== flowTab.id && styles.panelHidden)}
						role="tabpanel"
						id={`panel-${flowTab.id}`}
						testId={`panel-${flowTab.id}`}
						aria-labelledby={`rovo-tab-${flowTab.id}`}
						aria-hidden={selectedTab !== flowTab.id}
					>
						{visitedTabs.has(flowTab.id) ? flowTab.content : null}
					</Box>
				)}
				{/* Overlay tabs: mounted but out of flow when not selected */}
				{overlayTabs.map((tab) => (
					<Box
						key={tab.id}
						xcss={cx(styles.overlayPanel, selectedTab !== tab.id && styles.panelDisplayNone)}
						role="tabpanel"
						id={`panel-${tab.id}`}
						testId={`panel-${tab.id}`}
						aria-labelledby={`rovo-tab-${tab.id}`}
						aria-hidden={selectedTab !== tab.id}
					>
						{visitedTabs.has(tab.id) ? tab.content : null}
					</Box>
				))}
			</Box>
		</Box>
	);
};
